import React, { useEffect, useState } from "react";
import { PartyStore } from "@store/PartyStore.js";
import { InventoryStore } from "@store/InventoryStore.js";
import { CampStashStore } from "@store/CampStashStore.js";
import { EventBus } from "@engine/EventBus.js";
import {
  totalInventoryWeight,
  carryCapacity,
  addItem,
  removeItem,
} from "@engine/inventory/CharacterInventory.js";
import type { PlayerCharacter, Character } from "@engine/types/entity.js";
import type { ItemStack } from "@engine/types/items.js";

function silhouettePath(characterClass: string): string {
  const KNOWN = ["fighter", "wizard", "rogue", "cleric", "ranger", "paladin", "bard", "druid", "monk", "sorcerer", "warlock", "barbarian"];
  const slug = characterClass.toLowerCase();
  return `/engine/portraits/silhouettes/${KNOWN.includes(slug) ? slug : "adventurer"}.svg`;
}

function WeightBar({ current, max }: { current: number; max: number }) {
  const pct = max > 0 ? Math.min(100, (current / max) * 100) : 0;
  const color = pct > 95 ? "#f44336" : pct > 75 ? "#ff9800" : "#4caf50";
  return (
    <div>
      <div style={{ background: "#333", borderRadius: 3, height: 8, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width 0.2s" }} />
      </div>
      <div style={{ color: "#aaa", fontSize: 10, fontFamily: "monospace", marginTop: 3 }}>
        {current.toFixed(1)} / {max} lbs
      </div>
    </div>
  );
}

/**
 * U27 — Camp stash screen.
 *
 * Two-pane layout: left = selected character's carried items, right = infinite
 * camp chest. Deposit: drag from left → right (click item → "Stash" button).
 * Withdraw: click item → "Take" button (subject to carry capacity; toast on
 * refusal). Headshot strip selects whose bag to show.
 */
export function CampStashScreen({ onClose }: { onClose: () => void }) {
  const [members, setMembers] = useState<readonly PlayerCharacter[]>(() =>
    PartyStore.getMembers(),
  );
  const [activeIdx, setActiveIdx] = useState(0);
  const [stashStacks, setStashStacks] = useState<readonly ItemStack[]>(() =>
    CampStashStore.list(),
  );
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    const unsub = EventBus.on("ui:party_update", () => {
      setMembers([...PartyStore.getMembers()]);
      setStashStacks([...CampStashStore.list()]);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const member = members[activeIdx] ?? members[0];
  if (!member) return null;

  const inv = InventoryStore.get(member.instanceId);
  const weight = totalInventoryWeight(inv);
  const capacity = carryCapacity(member.abilities.STR);

  const deposit = (stack: ItemStack) => {
    const { inventory: next } = removeItem(inv, stack.item.id, stack.quantity);
    InventoryStore.set(member.instanceId, next);
    CampStashStore.deposit(stack);
    setStashStacks([...CampStashStore.list()]);
  };

  const withdraw = (stack: ItemStack) => {
    const { inventory: next, added, refused } = addItem(
      inv,
      stack.item,
      stack.quantity,
      member.abilities.STR,
    );
    if (added === 0) {
      setToast(`${member.name} is over capacity.`);
      return;
    }
    CampStashStore.withdraw(stack.item.id, added);
    InventoryStore.set(member.instanceId, next);
    if (refused > 0) setToast(`${refused} item(s) wouldn't fit.`);
    setStashStacks([...CampStashStore.list()]);
  };

  const itemRow = (
    stack: ItemStack,
    actionLabel: string,
    onAction: (s: ItemStack) => void,
  ) => (
    <div
      key={stack.item.id}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "3px 4px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        color: "#ddd",
        fontSize: 11,
        fontFamily: "monospace",
      }}
    >
      <span>
        {stack.item.name}
        {stack.quantity > 1 && (
          <span style={{ color: "#888", marginLeft: 4 }}>×{stack.quantity}</span>
        )}
      </span>
      <button
        type="button"
        onClick={() => onAction(stack)}
        style={{
          padding: "2px 8px",
          fontSize: 10,
          fontFamily: "monospace",
          background: "#2a4060",
          color: "#c8e6ff",
          border: "1px solid rgba(100,160,220,0.4)",
          borderRadius: 3,
          cursor: "pointer",
          flexShrink: 0,
          marginLeft: 8,
        }}
      >
        {actionLabel}
      </button>
    </div>
  );

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 300,
        pointerEvents: "auto",
      }}
    >
      <div
        style={{
          background: "#111",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: 8,
          padding: 24,
          width: "min(95vw, 720px)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ margin: 0, color: "white", fontFamily: "monospace", fontSize: 18, letterSpacing: 2 }}>
            CAMP STASH
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "4px 12px",
              fontFamily: "monospace",
              fontSize: 12,
              background: "rgba(0,0,0,0.4)",
              color: "#aaa",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 3,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div style={{
            background: "rgba(200,50,50,0.8)",
            color: "white",
            fontFamily: "monospace",
            fontSize: 11,
            padding: "4px 12px",
            borderRadius: 4,
            marginBottom: 8,
          }}>
            {toast}
          </div>
        )}

        {/* Two-pane content */}
        <div style={{ display: "flex", gap: 12, flex: 1, minHeight: 0 }}>
          {/* Left — character bag */}
          <div style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            background: "rgba(0,0,0,0.3)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 4,
            padding: 10,
          }}>
            <div style={{ color: "#aaa", fontFamily: "monospace", fontSize: 10, marginBottom: 6, letterSpacing: 1 }}>
              {member.name.toUpperCase()}&apos;S BAG
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {inv.stacks.length === 0 && (
                <div style={{ color: "#555", fontFamily: "monospace", fontSize: 11, padding: 4 }}>Empty.</div>
              )}
              {inv.stacks.map((stack) => itemRow(stack, "Stash", deposit))}
            </div>
            <div style={{ marginTop: 8 }}>
              <WeightBar current={weight} max={capacity} />
            </div>
          </div>

          {/* Right — camp chest */}
          <div style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            background: "rgba(0,0,0,0.3)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 4,
            padding: 10,
          }}>
            <div style={{ color: "#aaa", fontFamily: "monospace", fontSize: 10, marginBottom: 6, letterSpacing: 1 }}>
              CAMP CHEST (infinite)
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {stashStacks.length === 0 && (
                <div style={{ color: "#555", fontFamily: "monospace", fontSize: 11, padding: 4 }}>Empty.</div>
              )}
              {stashStacks.map((stack) => itemRow(stack, "Take", withdraw))}
            </div>
          </div>
        </div>

        {/* Headshot strip */}
        <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "center" }}>
          {members.map((m, i) => (
            <button
              key={m.instanceId}
              type="button"
              data-testid={`stash-headshot-${m.instanceId}`}
              onClick={() => setActiveIdx(i)}
              title={m.name}
              style={{
                width: 48,
                height: 48,
                padding: 0,
                border: `2px solid ${i === activeIdx ? "#7dcfff" : "rgba(255,255,255,0.2)"}`,
                borderRadius: 4,
                background: "#c9c1b0",
                cursor: "pointer",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img
                src={(m as Partial<Character>).portrait ?? silhouettePath(m.characterClass)}
                alt={m.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = silhouettePath("adventurer");
                }}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
